import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, AsyncValidator, ControlValueAccessor, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { EditorComponent } from 'ngx-monaco-editor-v2';
import { MonacoConfigService } from '../../services/monaco-editor/monaco-config-service';

declare const monaco: any;

@Component({
    selector: 'json-editor',
    templateUrl: './json-editor.component.html',
    styleUrls: ['./json-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => JsonEditorComponent),
            multi: true,
        },
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: forwardRef(() => JsonEditorComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class JsonEditorComponent implements AsyncValidator, ControlValueAccessor, OnChanges {
  @Input('value') set externalValue(value: unknown) {
    this.applyIncomingValue(value);
  }
  @Input() validationSchema?: any;
  @Input() readOnly = false;

  private static instanceCounter = 0;
  private readonly instanceId = JsonEditorComponent.instanceCounter++;
  private readonly markerValidationDelay = 1250;
  private isDisabled = false;
  private editor: any;
  private lastEmittedEditorText = '{}';
  editorText = '{}';
  private onChange: (value: unknown | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  @ViewChild(EditorComponent, { static: false })
  monacoComponent: EditorComponent;

  editorOptions: any = {
    language: 'json',
    roundedSelection: true,
    automaticLayout: true,
    autoIndent: 'full'
  };

  constructor(
    private readonly monacoConfigService: MonacoConfigService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readOnly']) {
      this.updateEditorReadOnlyState();
    }

    if (changes['validationSchema']) {
      const model = this.editor?.getModel();
      if (model && this.validationSchema) {
        this.registerMonacoJsonSchemaValidator(model);
      }
      this.triggerValidation();
    }
  }

  editorInit(editor: any): void {
    this.editor = editor;

    const model = editor.getModel();
    if (model) {
      this.editorText = this.normalizeValue(this.editorText);
      model.setValue(this.editorText);

      if (this.validationSchema) {
        this.registerMonacoJsonSchemaValidator(model);
      }
    }

    this.updateEditorReadOnlyState();
    this.triggerValidation();

    editor.onDidChangeModelContent(() => {
      setTimeout(() => this.onEditorValueChange(editor.getValue()));
    });
  }

  writeValue(value: unknown): void {
    this.applyIncomingValue(value);
  }

  registerOnChange(fn: (value: unknown | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): Promise<ValidationErrors | null> {
    const parseResult = this.parseEditorValue(this.editorText);
    if (parseResult.error) {
      return Promise.resolve({
        jsonSyntax: [parseResult.error]
      });
    }

    if (!this.validationSchema || !this.editor) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      setTimeout(() => {
        const model = this.editor?.getModel();
        const markers = model ? monaco.editor.getModelMarkers({ resource: model.uri, owner: 'json' }) : [];
        const messages = markers.map((marker: any) => `Line ${marker.startLineNumber}, Column ${marker.startColumn}: ${marker.message}`);

        resolve(messages.length ? { jsonSchema: messages } : null);
      }, this.markerValidationDelay);
    });
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.updateEditorReadOnlyState();
  }

  private registerMonacoJsonSchemaValidator(model: any): void {
    this.monacoConfigService.addSchema({
      uri: `mySchema-${this.instanceId}`,
      fileMatch: [model.uri.toString()],
      schema: this.validationSchema
    });
  }

  private onEditorValueChange(value: string): void {
    if (value !== this.lastEmittedEditorText) {
      this.editorText = value;
      this.lastEmittedEditorText = value;
      this.onChange(this.parseEditorValue(value).value);
    }

    this.onTouched();
    this.triggerValidation();
    this.cdRef.detectChanges();
  }

  private normalizeValue(value: unknown): string {
    if (value == null || value === '') {
      return '{}';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    const stringValue = String(value);

    try {
      return JSON.stringify(JSON.parse(stringValue), null, 2);
    } catch {
      return stringValue;
    }
  }

  private applyIncomingValue(value: unknown): void {
    const normalizedValue = this.normalizeValue(value);

    this.editorText = normalizedValue;
    this.lastEmittedEditorText = normalizedValue;

    if (this.editor) {
      this.editor.setValue(normalizedValue);
    }

    this.triggerValidation();
  }

  private parseEditorValue(value: string): { error?: string; value: unknown | null } {
    try {
      const parsedValue = JSON.parse(value);
      const isArray = Array.isArray(parsedValue);
      const isObject = !!parsedValue && typeof parsedValue === 'object';

      return isArray || isObject
        ? { value: parsedValue }
        : { value: null, error: 'JSON value must be an object or array.' };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON syntax.';
      return {
        value: null,
        error: `Invalid JSON syntax: ${message}`
      };
    }
  }

  private triggerValidation(): void {
    this.onValidatorChange();
  }

  private updateEditorReadOnlyState(): void {
    this.editor?.updateOptions({ readOnly: this.readOnly || this.isDisabled });
  }
}
